/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect } from 'react';

/**
 * Logo image variants that back every loading indicator (splash, LoadingPage,
 * LogoRingSpinner). Both are warmed so a theme switch never triggers a
 * cold-fetch pop-in.
 */
export const LOGO_PRELOAD_SOURCES = [
  '/images/logo/housien-light.png',
  '/images/logo/housien-dark.png',
] as const;

let warmed = false;

/**
 * Resets the once-per-session warm guard. Test-only: lets each test observe a
 * fresh cold-start rather than inheriting the previous test's warmed state.
 */
export function resetLogoPreloadForTests(): void {
  warmed = false;
}

/**
 * Decodes a single image source into the browser cache, resolving the decoded
 * bitmap ahead of the first paint so later `<img>` renders are instant.
 *
 * @param src - Absolute path to the logo image to warm.
 */
function decodeIntoCache(src: string): void {
  const img = new Image();
  img.decoding = 'async';
  img.fetchPriority = 'high';
  img.src = src;
  void img.decode().catch(() => undefined);
}

/**
 * Appends a high-priority `<link rel="preload">` for a logo source when one is
 * not already present, so the network fetch starts as early as possible.
 *
 * @param src - Absolute path to the logo image to preload.
 */
function appendPreloadLink(src: string): void {
  const selector = `link[rel="preload"][as="image"][href="${src}"]`;
  if (document.head.querySelector(selector)) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.setAttribute('as', 'image');
  link.href = src;
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
}

/**
 * Warms every logo variant into the browser image cache exactly once per app
 * session. Mounted high in the tree (MainProvider) so any loading overlay shown
 * afterwards renders its logo instantly instead of fetching it on demand.
 */
export function useLogoPreload(): void {
  useEffect(() => {
    if (warmed) return;
    warmed = true;
    LOGO_PRELOAD_SOURCES.forEach((src) => {
      appendPreloadLink(src);
      decodeIntoCache(src);
    });
  }, []);
}
